import { useCallback, useEffect, useState } from "react";
import { theme, card, btnPrimary, btnSecondary, inputStyle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { BUREAU_CHANGE_STATUS_LABELS, fmtChangeDate, type BureauChangeRequest, type BureauDetails } from "../lib/bureauChangeRequests";
import { ChangeLines, statusChipStyle } from "./BureauChangeRequestPanel";

// Platform Admin → Bureau Changes. Requests from bureau administrators to
// change their bureau's name, accreditation body, accreditation or country.
// Approve applies the change; decline records a note the bureau sees. The API
// refuses anything no longer pending (409), so a stale queue reloads instead.

interface QueueRow extends BureauChangeRequest {
  bureau: BureauDetails & { isDemo: boolean; bureauStatus: string };
}

export function BureauChangeRequestsAdminTab({ onCountsChanged }: { onCountsChanged?: () => void }) {
  const [showAll, setShowAll] = useState(false);
  const [rows, setRows] = useState<QueueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    api.get<{ requests: QueueRow[] }>(`/admin/bureau-change-requests${showAll ? "?status=all" : ""}`)
      .then((d) => { setRows(d.requests); setError(null); })
      .catch((e: unknown) => setError(apiErrorMessage(e, "Failed to load bureau change requests.")));
  }, [showAll]);
  useEffect(load, [load]);

  const decide = async (r: QueueRow, decision: "approve" | "decline") => {
    const note = (notes[r.id] ?? "").trim();
    if (decision === "decline" && !note) {
      setRowError((p) => ({ ...p, [r.id]: "Add a note saying why; the bureau sees it." }));
      return;
    }
    if (decision === "approve" && !window.confirm(`Apply this change to ${r.bureau.name}?`)) return;
    setBusyId(r.id); setRowError((p) => ({ ...p, [r.id]: "" }));
    try {
      await api.post(`/admin/bureau-change-requests/${r.id}/${decision}`, note ? { note } : {});
      load();
      onCountsChanged?.();
    } catch (e) {
      setRowError((p) => ({ ...p, [r.id]: apiErrorMessage(e, "The decision could not be saved.") }));
      load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>Bureau Changes</div>
          <div style={{ fontSize: 13, color: theme.color.muted, lineHeight: 1.5, maxWidth: 640 }}>
            Bureau administrators cannot change their bureau's name, accreditation or country themselves. Check the reason (and any evidence they send) before approving; approval takes effect immediately.
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: theme.color.text, cursor: "pointer" }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} style={{ accentColor: theme.color.accent }} />
          Include decided and withdrawn
        </label>
      </div>

      {error && <div style={{ fontSize: 13, color: theme.color.dangerText }}>{error}</div>}
      {!rows && !error && <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>}
      {rows && rows.length === 0 && (
        <div style={{ fontSize: 13, color: theme.color.subtle }}>{showAll ? "No bureau change requests yet." : "No bureau change requests awaiting review."}</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {rows?.map((r) => (
          <div key={r.id} style={{ ...card, padding: "16px 20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading }}>
                  {r.bureau.name}
                  {r.bureau.isDemo && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Demo</span>}
                </div>
                <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>
                  Asked {fmtChangeDate(r.createdAt)} by {r.requestedBy ? `${r.requestedBy.displayName} (${r.requestedBy.email})` : "a former member"}
                </div>
              </div>
              <span style={statusChipStyle(r.status)}>{BUREAU_CHANGE_STATUS_LABELS[r.status]}</span>
            </div>

            {/* A pending request is compared with the bureau as it is now; a decided one with what it was. */}
            <ChangeLines request={r} current={r.status === "pending" ? r.bureau : undefined} />
            <div style={{ fontSize: 13, color: theme.color.text, marginTop: 10, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              <span style={{ color: theme.color.subtle }}>Reason: </span>{r.reason}
            </div>

            {r.status === "pending" ? (
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                <textarea
                  rows={2}
                  maxLength={2000}
                  placeholder="Note to the bureau (required to decline)"
                  value={notes[r.id] ?? ""}
                  onChange={(e) => setNotes((p) => ({ ...p, [r.id]: e.target.value }))}
                  style={{ ...inputStyle, width: "100%", resize: "vertical", boxSizing: "border-box" }}
                />
                {rowError[r.id] && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{rowError[r.id]}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" disabled={busyId === r.id} onClick={() => decide(r, "approve")} style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12 }}>
                    Approve
                  </button>
                  <button type="button" disabled={busyId === r.id} onClick={() => decide(r, "decline")} style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12 }}>
                    Decline
                  </button>
                </div>
              </div>
            ) : (
              (r.reviewedBy || r.reviewNote) && (
                <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 10, whiteSpace: "pre-wrap" }}>
                  {r.reviewedBy ? `${BUREAU_CHANGE_STATUS_LABELS[r.status]} by ${r.reviewedBy.displayName}${r.reviewedAt ? ` on ${fmtChangeDate(r.reviewedAt)}` : ""}` : ""}
                  {r.reviewNote ? `${r.reviewedBy ? ": " : ""}${r.reviewNote}` : ""}
                </div>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
